"use client"
import React, { useState, useMemo} from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog"
import { Button } from "../../../Components/UI/button"
import { TooltipProvider } from "../../../Components/UI/tooltip"
import { Card, CardContent } from "../../../Components/UI/card"
import { X, ArrowLeft, Pencil, Ban, RotateCw, Plus, ListTodo, Check } from "lucide-react"
import { toast } from "sonner"
import { useDesktop } from "../../../Components/Common/IsDesktop"
import { getCurrencySymbol, currentUserHasPermission, getRelativeDateText, getColorClass, getTaskColor } from "../../../Utils/Utilities"
import ContractEntry from "../../Contracts/ContractEntry"
import TaskEntry from "../../Tasks/TaskEntry"
import { format } from 'date-fns'
import { useSelector } from "react-redux"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../Components/UI/table"
import { useRef, useEffect } from "react"
import { Tooltip, TooltipTrigger, TooltipContent } from "../../../Components/UI/tooltip"
import { getContractsArr, getThreadTypesArr, getUsersArr } from "../../../Store/Reducers"
import { completeTaskAPI, cancelTaskAPI } from "../../../Modules/Tasks/TasksAPI"
import { useActiveThreadsByConnID } from "../../../Hooks/useConnectionData"
import { getThreadTypeMeta } from "../../../Utils/Utilities"
import { Badge } from "../../../Components/UI/badge"
import ThreadSahiIcon from "../../../Components/Common/ThreadSahiIcon"
import { HoverCard, HoverCardContent, HoverCardTrigger  } from "@radix-ui/react-hover-card"
import { AlertBox } from "../../../Components/Common/AlertBox"
import { TaskCompleteDialog } from "../../../Components/Common/TaskCompleteDialog"

const ContractDetails = ({ isOpen, onClose, contract, connection, currencySymbol, onContractUpdate }) => {
    const isDesktop = useDesktop();
    const {data:allTasksData} = useSelector((state) => state.taskData);
    const users = useSelector(getUsersArr);
    const allContracts = useSelector(getContractsArr);
    // State to store filtered contract tasks
    const [contractTasks, setContractTasks] = useState([]);
    const [refreshTasks, setRefreshTasks] = useState(false);
    const activeThreads = useActiveThreadsByConnID(contract?.ConnID);
    const getThreadsData = () => activeThreads;
    const threadTypesData = useSelector(getThreadTypesArr);
    const [expandedTaskThreads, setExpandedTaskThreads] = useState(new Set());
    const [selectedTaskForAction, setSelectedTaskForAction] = useState(null)
    
    // Filter and update contract tasks whenever dependencies change
    useEffect(() => {
        const contractTaskIDs = allContracts.find(ctrc => ctrc.ContractID == contract?.ContractID)
                                    ?.TaskIDs?.split(",").filter(ID => ID !== "") || [];
        const filteredTasks = allTasksData?.filter(task => {
            if (task.Status !== 1) return false;
            return contractTaskIDs.includes(String(task.TaskID));
        }) || [];
        
        setContractTasks(filteredTasks);
        
        // Reset refresh flag if it was triggered
        if (refreshTasks) {
            setRefreshTasks(false);
        }
    }, [allTasksData, contract?.TaskIDs, refreshTasks]);

    const [hasScrollbar, setHasScrollbar] = useState(false);
    const scrollableRef = useRef(null);

    // Check for scrollbar to adjust header margin
    useEffect(() => {
      const checkScrollbar = () => {
        if (scrollableRef.current) {
          const hasVerticalScrollbar = scrollableRef.current.scrollHeight > scrollableRef.current.clientHeight;
          setHasScrollbar(hasVerticalScrollbar);
        }
      };

      checkScrollbar();
      const timeoutId = setTimeout(checkScrollbar, 100);
      return () => clearTimeout(timeoutId);
    }, [contractTasks]);
    
    // Common Tailwind classes to prevent repetition
    const contractDetailsClasses = {
        button: "h-7 w-7 p-0 shrink-0",
        badgeBase: "inline-block bg-gray-100 px-3 py-2 rounded text-sm text-foreground whitespace-nowrap truncate",
        dialogContent: "w-full max-w-6xl h-auto max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0 bg-gray-50 min-w-[930px]",
        mobileDialogContent: "w-full h-full max-w-full max-h-full p-0 overflow-hidden flex flex-col gap-0 bg-gray-50",
        headerMobile: "bg-slate-800 text-white px-4 py-3 flex-shrink-0",
        headerDesktop: "bg-slate-800 text-white px-6 py-4 flex-shrink-0",
        hoverBg: "hover:bg-gray-50 cursor-pointer",
        buttonAction: "bg-sahi-green border hover:border-sahi-green hover:text-sahi-green",
        buttonCancel: "bg-sahi-red border hover:border-sahi-red hover:text-sahi-red",
        buttonSecondary: "text-sm px-3 py-1.5 h-auto",
        contentScrollable: "flex-1 overflow-y-auto bg-gray-50",
        loader: "h-3 w-3 animate-spin border-2 border-white border-t-transparent rounded-full mr-1",
        sectionCard: "bg-sahi-white rounded-lg border border-sahi-border-color",
        cardSpacing: "mx-3 mt-3",
        cardBase: "bg-white sahi-radius border border-sahi-border-color !max-h-[150px]",
        flexBetween: "flex items-center justify-between",
        textColor: "text-color ",
        tableCell: "px-3 py-2 text-sm",
        truncate: "truncate",
        hoverBg: "hover:bg-gray-50 cursor-pointer",
        trueActionButton: " h-6 w-6 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-green-light",
        falseActionButton: "h-6 w-6 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-red-light",
        editActionButton: "h-6 w-6 p-0 flex items-center justify-center hover:shadow-sm transform-gpu transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-blue-light",
        badgeBaseThr: "sahi-badge transition ease-in-out delay-150 hover:-translate-y-1 duration-200 border-foreground/10 text-foreground bg-card hover:bg-card/80"
    }

    // Permission checks
    const canEditContracts = currentUserHasPermission('ConnEdit')

    // Loading states for actions
    const [actionLoading, setActionLoading] = useState({
        cancel: false,
        renew: false,
    })

    // Contract entry state
    const [isContractEntryOpen, setIsContractEntryOpen] = useState(false)
    const [contractEntryMode, setContractEntryMode] = useState('edit')
    
    // Task entry state
    const [isTaskEntryOpen, setIsTaskEntryOpen] = useState(false)
    const [editingTaskID, setEditingTaskID] = useState(null)

    // Dialog states
    const [dialogStates, setDialogStates] = useState({
        confirmCompleteTask: false,
        confirmCancelTask: false
    })

    if (!contract) return null

    // Format task data for TaskEntry
    const formatTaskData = (task) => {
        return {
            taskID: task.TaskID,
            connID: task.ConnID,
            taskName: task.Task, // task.Task contains the task name
            description: task.Description || task.Task, // Use Description if available
            dueDT: task.DueDT,
            dueTime: task.DueDT ? format(new Date(task.DueDT), 'HH:mm') : '11:00',
            pocUserID: task.POCUserID,
            priority: task.Priority || 'M',
            taskType: task.TaskType,
            threadIDs: task.ThreadIDs ? task.ThreadIDs.split(',').filter(id => id.trim()) : [], // Convert to array
            ctrcIDs: task.CtrcIDs ? task.CtrcIDs.split(',').filter(id => id.trim()) : [], // Convert to array
        };
    };

    // Handle task entry open
    const onTaskEntryOpen = () => {
        setIsTaskEntryOpen(true);
        setEditingTaskID(null);
    };

    // Handle task edit
    const handleTaskEdit = (taskID) => {
        setEditingTaskID(taskID);
        setIsTaskEntryOpen(true);
    };

    // Handle contract actions
    const handleContractAction = async (action) => {
        setActionLoading(prev => ({ ...prev, [action]: true }))

        try {
            setContractEntryMode(action)
            setIsContractEntryOpen(true)
        } catch (error) {
            console.error(`Error performing ${action} action:`, error)
            toast.error(`Failed to ${action} contract. Please try again.`)
        } finally {
            setActionLoading(prev => ({ ...prev, [action]: false }))
        }
    }

    const getContractEntryData = () => {
        // Helper function to safely format dates
        const formatDateSafe = (dateValue) => {
            if (!dateValue) return "";
            try {
                const date = new Date(dateValue);
                return isNaN(date.getTime()) ? "" : format(date, 'dd-MMM-yy');
            } catch (e) {
                console.error('Error formatting date:', e);
                return "";
            }
        };

        const formatDateAPISource = (dateValue) => {
            if (!dateValue) return "";
            try {
                const date = new Date(dateValue);
                return isNaN(date.getTime()) ? "" : format(date, 'yyyy-MM-dd');
            } catch (e) {
                console.error('Error formatting API date:', e);
                return "";
            }
        };

        return {
            contractID: contract.ContractID || contract.ID,
            connection: connection?.ConnID || contract.ConnID,
            contactPerson: contract.ContactName || connection?.ContactName || "",
            contractNo: contract.ContractNo || "",
            contractType: contract.ContractType || "",
            contractTypeID: contract.ContractTypeID || 0,
            product: contract.Product || "",
            startDate: formatDateSafe(contract.StartDT),
            startDateAPI: formatDateAPISource(contract.StartDT),
            expireDate: formatDateSafe(contract.ExpiryDT),
            expireDateAPI: formatDateAPISource(contract.ExpiryDT),
            charges: contract.ContractCharges || "",
            renewal: contract.RenewalCharges || "",
            notes: contract.Notes || "",
            fileName: contract.Attachment || "",
            sellerAgencyID: contract.SellerAgencyID || 0,
            buyerAgencyID: contract.BuyerAgencyID || 0,
            contractTypeSelection: contract.IsSeller === 0 ? 'purchase' : 'sales'
        }
    }


    // Helper function to create badge data array
    const getBadgeData = () => {
        const badges = []

        // Business Name
        if (connection?.BizName || contract.BizName) {
            badges.push({
                label: "Business:",
                value: connection?.BizName || contract.BizName,
                className: "text-gray-900 truncate w-full",
            })
        }
        if (connection?.BizName || contract.BizName) {
            badges.push({
                label: "Contract No:",
                value: contract?.ContractNo,
                className: "text-gray-900",
            })
        }
        // Contract Type
        if (contract.ContractType) {
            badges.push({
                label: "Contract:",
                value: contract.ContractType,
                className: "text-gray-900",
            })
        }

        // 
        if (contract.Product) {
            badges.push({
                label: "Offering:",
                value: contract.Product,
                className: "text-gray-900",
            })
        }

        // Contact Person
        if (contract.ContactName) {
            badges.push({
                label: "Contact:",
                value: contract.ContactName,
                className: "text-gray-900",
            })
        }

        // Start Date
        if (contract.StartDT) {
            badges.push({
                label: "Start:",
                value: getRelativeDateText(contract.StartDT),
                className: `${getColorClass(getTaskColor(contract.StartDT))}`,
            })
        }

        // Expiry Date
        if (contract.ExpiryDT) {
            badges.push({
                label: "Expiry:",
                value: getRelativeDateText(contract.ExpiryDT),
                className: `${getColorClass(getTaskColor(contract.ExpiryDT))}`,
            })
        }

        // Contract Charges
        if (contract.ContractCharges) {
            badges.push({
                label: "Charges:",
                value: `${getCurrencySymbol(contract.CurrencyID) || currencySymbol} ${parseFloat(contract.ContractCharges).toLocaleString()}`,
                className: "text-gray-900",
            })
        }

        // Renewal Charges
        if (contract.RenewalCharges) {
            badges.push({
                label: "Renewal:",
                value: `${getCurrencySymbol(contract.CurrencyID) || currencySymbol} ${parseFloat(contract.RenewalCharges).toLocaleString()}`,
                className: "text-gray-900",
            })
        }

        return badges
    }
    
    // Get executive name by user ID
    const getExecutiveName = (pocUserID, badge = false) => {
        const user = users.find((user) => user.userID === pocUserID)
        return user ? user.name : badge ? "Not assigned" : "--"
    }
    
    // Unified handler for all item actions
    const handleItemAction = async (action, item, type, event = null) => {
        if (event) event.stopPropagation();
        
        switch (action) {
            case 'COMPLETE':
                if (type === 'task') {
                    setSelectedTaskForAction(item);
                    setDialogStates(prev => ({ ...prev, confirmCompleteTask: true }));
                }
                break;
            case 'CANCEL':
                if (type === 'task') {
                    setSelectedTaskForAction(item);
                    setDialogStates(prev => ({ ...prev, confirmCancelTask: true }));
                }
                break;
            case 'RECORD_AND_COMPLETE':
                if (type === 'task') {
                    setDialogStates(prev => ({ ...prev, confirmCompleteTask: false, recordEntry: true }));
                }
                break;
            default:
                console.warn(`Unknown action: ${action}`);
                break;
        }
    };

    const handleTaskAction = async (action) => {
        if (!selectedTaskForAction) return;
        
        let result = null;
        const apiParams = {
            taskID: selectedTaskForAction.TaskID,
            connID: selectedTaskForAction.ConnID,
            pocUserID: selectedTaskForAction.POCUserID,
            taskName: selectedTaskForAction.Task,
            description: selectedTaskForAction.Task,
            priority: selectedTaskForAction.Priority,
            status: selectedTaskForAction.Status,
            taskType: selectedTaskForAction.TaskType,
            threads: selectedTaskForAction.ThreadIDs,
            contracts: selectedTaskForAction.CtrcIDs,
            AgencyID: selectedTaskForAction.AgencyID
        };
        
        try {
            if (action === 'COMPLETE') {
                result = await completeTaskAPI(apiParams);
                if (result.error === false && result.message === "Success") {
                    toast.success('Task completed.');
                } else {
                    toast.error(result.message);
                }
            } else if (action === 'CANCEL') {
                result = await cancelTaskAPI(apiParams);
                if (result.error === false && result.message === "Success") {
                    toast.success('Task cancelled.');
                } else {
                    toast.error(result.message);
                }
            }
        } catch (error) {
            console.error('Error performing task action:', error);
            toast.error(`Failed to ${action === 'COMPLETE' ? 'complete' : 'cancel'} task. Please try again.`);
        } finally {
            setDialogStates(prev => ({ 
                ...prev, 
                confirmCompleteTask: false, 
                confirmCancelTask: false 
            }));
            setSelectedTaskForAction(null);
        }
    };

    const openTaskEntry = () => {
        if(contractTasks.length >= 3){
            toast.error("You can add a maximum of three follow-up tasks at a time.");
            return;
        }
        setIsTaskEntryOpen(true);
    }

    const TasksTable = () => {
        const tasks = contractTasks || [];
        const toggleTaskThreads = (taskID) => {
            setExpandedTaskThreads(prev => {
                const newSet = new Set(prev);
                if (newSet.has(taskID)) {
                    newSet.delete(taskID);
                } else {
                    newSet.add(taskID);
                }
                return newSet;
            });
        };

        return (
        <div className="space-y-3 px-4 pb-4">
            <div className={contractDetailsClasses.flexBetween}>
            <h3 className={`text-lg font-semibold ${contractDetailsClasses.textColor}`}>
                Tasks
            </h3>
            <div className="flex items-center gap-2">
                <Button
                className="flex items-center"
                onClick={() => openTaskEntry()}
                >
                <Plus className="h-4 w-4" />
                  Add Task
                </Button>
            </div>
            </div>

            {tasks.length > 0 ? (
            <Card className="bg-white overflow-hidden lg:!pt-0">
                <CardContent className="p-0 relative">
                {/* Container for both header and body to maintain alignment */}
                <div className="table-container">
                    {/* Fixed header */}
                    <div
                    className="bg-white border-b border-gray-200 overflow-hidden"
                    style={{
                        marginRight: hasScrollbar ? '8px' : '0px',
                        transition: 'margin-right 0.1s ease'
                    }}
                    >
                    <Table className="table-fixed-layout">
                        <colgroup className="tasks-table-cols">
                        <col style={{ width: '80px' }} />
                        <col style={{ width: 'auto' }} />
                        <col style={{ width: '140px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '110px' }} />
                        </colgroup>
                        <TableHeader className="!border-0">
                        <TableRow className="bg-white !border-0">
                            <TableHead className={`table-header-text ${contractDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Type</TableHead>
                            <TableHead className={`table-header-text ${contractDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Task</TableHead>
                            <TableHead className={`table-header-text ${contractDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Finish by</TableHead>
                            <TableHead className={`table-header-text ${contractDetailsClasses.textColor} whitespace-nowrap px-3 py-2 text-left`}>Executive</TableHead>
                            <TableHead className="table-header-text whitespace-nowrap px-3 py-2 text-left"></TableHead>
                        </TableRow>
                        </TableHeader>
                    </Table>
                    </div>
                </div>

                {/* Scrollable body */}
                <div ref={scrollableRef} className="table-scrollable-body !max-h-[150px] !overflow-y-auto">                
                    <Table className="table-fixed-layout">
                        <colgroup className="tasks-table-cols">
                        <col style={{ width: '80px' }} />
                        <col style={{ width: 'auto' }} />
                        <col style={{ width: '140px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '110px' }} />
                        </colgroup>
                        <TableBody>
                        {tasks.map((task, index) => {
                            const taskID = task.TaskID;
                            const isCompleted = task.Status === 2;
                            const isCancelled = task.Status === 0;
                            const canComplete = !isCompleted && !isCancelled;
                            const canCancel = !isCancelled;
                            const hasThreads = task.ThreadIDs && task.ThreadIDs.trim();
                            const isThreadsExpanded = expandedTaskThreads.has(taskID);

                            return (
                            <React.Fragment key={task.TaskID || index}>
                                <TableRow
                                className={`${contractDetailsClasses.hoverBg} border-b border-gray-100 ${hasThreads ? "!cursor-pointer" : "!cursor-default"}`}
                                onClick={() => hasThreads && toggleTaskThreads(taskID)}
                                >
                                <TableCell className={`${contractDetailsClasses.tableCell} ${contractDetailsClasses.textColor} text-left`}>
                                    <div className={contractDetailsClasses.truncate} title={task.TaskType || "General"}>
                                    {task.TaskType || "-"}
                                    </div>
                                </TableCell>

                                <TableCell className={`${contractDetailsClasses.tableCell} text-left`}>
                                    <div className="flex flex-col gap-1">
                                    <div
                                        className={`text-sm font-medium ${contractDetailsClasses.textColor} ${contractDetailsClasses.truncate}`}
                                        title={task.Task || "Untitled Task"}
                                        style={{ maxWidth: '100%' }}
                                    >
                                        {task.Task || "Untitled Task"}
                                        {(() => {
                                            const filteredCount = task.ThreadIDs?.split(',').filter((threadId) => {
                                                const thread = getThreadsData().find(t => t.ThreadID === parseInt(threadId.trim()));
                                                return thread && thread.StatusID === 0;
                                            }).length;

                                            return filteredCount > 0 ? (
                                                <span className="ml-2 text-xs text-gray-400">
                                                ({filteredCount})
                                                </span>
                                            ) : null;
                                        })()}
                                    </div>
                                    {isThreadsExpanded && hasThreads && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
                                            {task.ThreadIDs?.split(',').filter((threadId) => {
                                                const thread = getThreadsData().find(t => t.ThreadID === parseInt(threadId.trim()));
                                                return thread && thread.StatusID === 0;
                                            }).map((threadId, threadIndex) => {
                                            const thread = getThreadsData().find(t => t.ThreadID === parseInt(threadId.trim()));
                                            let displayText;
                                            let threadIcon = null;
                                            let threadTypeMeta = null;

                                            if (!thread) {
                                                displayText = `Thread #${threadId.trim()}`;
                                            } else {
                                                threadTypeMeta = getThreadTypeMeta(thread.TypeID || thread.typeID, threadTypesData);
                                                if (threadTypeMeta) {
                                                threadIcon = threadTypeMeta.Icon;
                                                }
                                                const threadType = threadTypeMeta ? threadTypeMeta.nameSng : (thread.TypeName || thread.Type || "General");
                                                const product = thread.MainProduct || thread.product || thread.Product || "No Product";
                                                displayText = `${threadType} - ${product}`;
                                            }

                                            const hasDateInfo = thread && (thread.targetDT || thread.sinceDT || thread.DueDT);
                                            const sinceDT = thread && thread.SinceDT ? getRelativeDateText(new Date(thread.SinceDT), false, false) : '';
                                            const targetDT = thread && thread.DueDT ? getRelativeDateText(new Date(thread.DueDT), false, false) : '';

                                            const BadgeContent = (
                                                <div className="w-full">
                                                <Button variant="ghost" size="sm" className="w-full justify-start p-0 h-auto">
                                                    <Badge className={`w-full justify-start h-7 ${contractDetailsClasses.badgeBaseThr} px-2`}>
                                                    {threadIcon && <ThreadSahiIcon tID={thread.TypeID} className='mr-2 flex-shrink-0' />}
                                                    <span className={`${contractDetailsClasses.truncate} text-xs`}>{displayText}</span>
                                                    </Badge>
                                                </Button>
                                                </div>
                                            );

                                            return hasDateInfo ? (
                                                <HoverCard key={`${taskID}-thread-${threadId.trim()}`}>
                                                <HoverCardTrigger asChild>{BadgeContent}</HoverCardTrigger>
                                                <HoverCardContent className="bg-white sahi-radius shadow-2xl p-3 w-40 text-left" side="top">
                                                    <h4 className="text-sm font-bold mb-2">
                                                    {threadTypeMeta ? threadTypeMeta.nameSng : (thread?.ThreadName || thread?.Type || "General")}
                                                    </h4>
                                                    <div>
                                                    {targetDT && (
                                                        <div>
                                                        <span className="text-muted-foreground">Due on: </span>
                                                        <span>{targetDT}</span>
                                                        </div>
                                                    )}
                                                    {sinceDT && (
                                                        <div>
                                                        <span className="text-muted-foreground">Since: </span>
                                                        <span>{sinceDT}</span>
                                                        </div>
                                                    )}
                                                    </div>
                                                </HoverCardContent>
                                                </HoverCard>
                                            ) : (
                                                <div key={`${taskID}-thread-${threadId.trim()}`}>{BadgeContent}</div>
                                            );
                                            })}
                                        </div>
                                    )}
                                    </div>
                                </TableCell>

                                <TableCell className={contractDetailsClasses.tableCell}>
                                    <div className="text-left">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                        <div className={`text-sm font-medium ${getColorClass(getTaskColor(task.DueDT))} whitespace-nowrap`}>
                                            {getRelativeDateText(task.DueDT)}
                                        </div>
                                        </TooltipTrigger>
                                        {task.DueDT && (
                                        <TooltipContent className="time-tooltip">
                                            <p>{format(new Date(task.DueDT), "hh:mm a").toUpperCase()}</p>
                                        </TooltipContent>
                                        )}
                                    </Tooltip>
                                    </div>
                                </TableCell>

                                <TableCell className={`${contractDetailsClasses.tableCell} ${contractDetailsClasses.textColor}`}>
                                    <div className={`text-left ${contractDetailsClasses.truncate}`}>
                                    {getExecutiveName(task.POCUserID)}
                                    </div>
                                </TableCell>

                                <TableCell className={contractDetailsClasses.tableCell}>
                                    <div className="flex items-start gap-[2px] xl:gap-2.5">
                                    {/* Complete Action */}
                                    {canComplete ? (
                                        <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                            variant="ghost"
                                            size="sm"
                                            className={contractDetailsClasses.trueActionButton}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleItemAction('COMPLETE', task, 'task', e);
                                            }}
                                            >
                                                <Check className="h-3 w-3 text-sahi-green" />
                                            
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Mark as complete</p>
                                        </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <span className="text-text-color h-6 w-6 flex items-center justify-center leading-none">-</span>
                                    )}

                                    {/* Cancel Action */}
                                    {canCancel ? (
                                        <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                            variant="ghost"
                                            size="sm"
                                            className={contractDetailsClasses.falseActionButton}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleItemAction('CANCEL', task, 'task', e);
                                            }}
                                            >
                                            <X className="h-3 w-3 text-sahi-red" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Cancel task</p>
                                        </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <span className="text-text-color h-6 w-6 flex items-center justify-center leading-none">-</span>
                                    )}

                                    {/* Edit Action - Always available */}
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className={contractDetailsClasses.editActionButton}
                                            onClick={(e) => {
                                            e.stopPropagation()
                                            handleTaskEdit(task.TaskID)
                                            }}
                                        >
                                            <Pencil className="h-3 w-3 text-sahi-blue" />
                                        </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                        <p>Edit Task</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    </div>
                                </TableCell>
                                </TableRow>
                            </React.Fragment>
                            );
                        })}
                        </TableBody>
                    </Table>
                </div>
                </CardContent>
            </Card>
            ) : (
            <div
                className={`${contractDetailsClasses.cardBase} ${contractDetailsClasses.hoverBg} transition-colors h-[100px]`}
                onClick={() => openTaskEntry()}
            >
                <div className="h-full flex items-start justify-start p-6">
                <div className="flex items-start space-x-4">
                    <ListTodo className="h-8 w-8 text-sahi-grey flex-shrink-0 mt-1" />
                    <div className="text-left">
                    <p className="text-sm font-medium mb-1">No tasks found</p>
                    <p className="text-xs text-gray-400">Click to add a task</p>
                    </div>
                </div>
                </div>
            </div>
            )}
        </div>
        );
    };

    // Mobile version
    if (!isDesktop) {
        return (
            <TooltipProvider>
                <Dialog open={isOpen} onOpenChange={onClose}>
                    <DialogContent className={contractDetailsClasses.mobileDialogContent}>
                        {/* Mobile Header */}
                        <DialogHeader className={contractDetailsClasses.headerMobile}>
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center ">
                                    <DialogClose onClick={onClose} className="p-2 -ml-2 text-white hover:bg-slate-700 rounded">
                                        <ArrowLeft className="h-6 w-6" />
                                    </DialogClose>
                                    <div>
                                        <DialogTitle className="text-lg font-medium text-left text-white">
                                            Contract - #{contract.ContractNo}
                                        </DialogTitle>
                                    </div>
                                </div>
                                {canEditContracts && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-white hover:bg-slate-700"
                                        onClick={() => {
                                            setContractEntryMode('edit')
                                            setIsContractEntryOpen(true)
                                        }}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </DialogHeader>

                        {/* Mobile Content */}
                        <div className={`${contractDetailsClasses.contentScrollable} p-3 space-y-4`}>
                            {/* Information Section */}
                            <Card className="border-gray-200 bg-white">
                                <CardContent className="p-3">
                                    <div className="space-y-3">
                                        {/* Badge Information */}
                                        <div className="flex flex-wrap gap-2 items-start">
                                            {getBadgeData().map((badge, index) => (
                                                <div key={index} className={contractDetailsClasses.badgeBase}>
                                                    <span>{badge.label} </span>
                                                    <span className={`font-semibold ${badge.className}`}>{badge.value}</span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Notes */}
                                        {contract.Notes && (
                                            <div className="inline-block bg-gray-100 px-3 py-2 rounded text-sm text-foreground break-words whitespace-normal max-w-full">
                                                <span>Notes: </span>
                                                <span className="font-semibold text-gray-900">{contract.Notes}</span>
                                            </div>
                                        )}

                                        {/* Action Buttons - Mobile */}
                                        <div className="grid grid-cols-2 gap-2">
                                            {/* Renew Button */}
                                            <Button
                                                onClick={() => handleContractAction('renew')}
                                                disabled={actionLoading.renew || contract.Status === 'Cancelled'}
                                                className={`${contractDetailsClasses.buttonAction} ${contractDetailsClasses.buttonSecondary} flex-1`}
                                            >
                                                {actionLoading.renew ? (
                                                    <div className={contractDetailsClasses.loader} />
                                                ) : (
                                                    <RotateCw className="h-3 w-3 " />
                                                )}
                                                Renew
                                            </Button>

                                            <Button
                                                onClick={() => handleContractAction('cancel')}
                                                disabled={actionLoading.cancel}
                                                className={`${contractDetailsClasses.buttonCancel} ${contractDetailsClasses.buttonSecondary} flex-1`}
                                            >
                                                {actionLoading.cancel ? (
                                                    <div className={contractDetailsClasses.loader} />
                                                ) : (
                                                    <Ban className="h-3 w-3 " />
                                                )}
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Tasks Section */}
                            <Card className="border-gray-200 bg-white">
                                <CardContent className="p-3">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-base font-semibold text-gray-900">
                                            {contractTasks.length > 0 ? `Tasks (${contractTasks.length})` : 'Tasks'}
                                        </h3>
                                        <div onClick={openTaskEntry} className="cursor-pointer">
                                            <Plus className="h-5 w-5" />
                                        </div>
                                    </div>
                                    
                                    {contractTasks.length > 0 ? (
                                        <div className="space-y-2">
                                            {contractTasks.map((task, index) => (
                                                <div 
                                                    key={task.TaskID || index}
                                                    className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                                                >
                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium text-sm text-gray-900 truncate">
                                                                {task.Task}
                                                            </p>
                                                            {task.TaskType && (
                                                                <span className="text-xs text-gray-500 capitalize">
                                                                    {task.TaskType}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                            {task.DueDT && (
                                                                <span className={`text-xs py-0.5 rounded ${getColorClass(getTaskColor(task.DueDT))}`}>
                                                                    {getRelativeDateText(task.DueDT)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                                        <span className="text-xs text-gray-500">
                                                            {getExecutiveName(task.POCUserID)}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            {/* Complete Button */}
                                                            {task.Status !== 2 && task.Status !== 0 && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 w-7 p-0 hover:bg-sahi-green-light"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleItemAction('COMPLETE', task, 'task', e);
                                                                    }}
                                                                >
                                                                    <Check className="h-3 w-3 text-sahi-green" />
                                                                </Button>
                                                            )}
                                                            
                                                            {/* Cancel Button */}
                                                            {task.Status !== 0 && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 w-7 p-0 hover:bg-sahi-red-light"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleItemAction('CANCEL', task, 'task', e);
                                                                    }}
                                                                >
                                                                    <X className="h-3 w-3 text-sahi-red" />
                                                                </Button>
                                                            )}
                                                            
                                                            {/* Edit Button */}
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-7 w-7 p-0 hover:bg-sahi-blue-light"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleTaskEdit(task.TaskID);
                                                                }}
                                                            >
                                                                <Pencil className="h-3 w-3 text-sahi-blue" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-6">
                                            <ListTodo className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-500">No tasks found</p>
                                            <p className="text-xs text-gray-400">Click Add Task to create one</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Contract Entry Dialog for Mobile */}
                <ContractEntry
                    isOpen={isContractEntryOpen}
                    onClose={() => setIsContractEntryOpen(false)}
                    contractID={contract.ContractID || contract.ID}
                    connID={connection?.ConnID || contract.ConnID}
                    companyName={connection?.BizName || contract.BizName || ""}
                    prefilledData={getContractEntryData()}
                    isRenewalMode={contractEntryMode === 'renew'}
                    isCancellationMode={contractEntryMode === 'cancel'}
                    onContractSaved={(updatedContract) => {
                        setIsContractEntryOpen(false)
                        if (onContractUpdate) {
                            onContractUpdate(updatedContract)
                        }
                        onClose()
                    }}
                />
                
                {/* Task Entry Dialog for Mobile */}
                <TaskEntry
                    key={editingTaskID || 'new-task'}
                    dialogTitle={editingTaskID ? 'Edit Task' : 'Add Task'}
                    triggerButton={false}
                    open={isTaskEntryOpen}
                    onOpenChange={(open) => {
                        setIsTaskEntryOpen(open);
                        if (!open) {
                            setEditingTaskID(null);
                        }
                    }}
                    prefilledData={editingTaskID ? (() => {
                        const task = contractTasks.find(t => t.TaskID === editingTaskID);
                        return task ? {
                            ...formatTaskData(task),
                            connID: connection?.ConnID || contract.ConnID,
                            connectionLocked: true,
                            connectionLabel: 'Contact',
                            bizName: connection?.BizName || contract.BizName || '',
                            contactName: contract.ContactName || '',
                            ctrcIDs: [String(contract.ContractID || contract.ID)],
                            contractLocked: true,
                        } : {};
                    })() : {
                        connID: connection?.ConnID || contract.ConnID,
                        connectionLocked: true,
                        connectionLabel: 'Contact',
                        bizName: connection?.BizName || contract.BizName || '',
                        contactName: contract.ContactName || '',
                        ctrcIDs: [String(contract.ContractID || contract.ID)],
                        contractLocked: true,
                    }}
                    onTaskAdded={() => {
                        setIsTaskEntryOpen(false);
                        setEditingTaskID(null);
                    }}
                />
                
                {/* Task Complete Alert */}
                <AlertBox
                    open={dialogStates.confirmCompleteTask}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCompleteTask: open }))}
                    title="Complete Task"
                    description={false ? "Do you want to add a record for this completed task?" : "Are you sure you want to complete this task?"}
                    actions={[
                        {
                            label: "Cancel",
                            variant: "cancel",
                            value: "cancel",
                            className: "w-full sm:w-auto",
                            icon: <X className="h-4 w-4" />
                        },
                        {
                            // label: "No, Just Complete",
                            label: "Complete Task",
                            variant: "action",
                            value: "complete",
                            className: "w-full sm:w-auto",
                            icon: <Check className="h-4 w-4" />
                        },
                        //  ...(selectedTaskForAction?.ConnID
                        //    ? [
                        //         {
                        //             label: "Yes, Add Record",
                        //             variant: "action",
                        //             value: "record",
                        //             className: "w-full sm:w-auto",
                        //             icon: <Plus className="h-4 w-4" />,
                        //         },
                        //     ]
                        //     : []),
                    ]}
                    onAction={(actionValue) => {
                        if (actionValue === "complete") {
                            handleTaskAction('COMPLETE');
                        } else if (actionValue === "record") {
                            handleItemAction('RECORD_AND_COMPLETE', selectedTaskForAction, 'task');
                        } else {
                            setSelectedTaskForAction(null);
                        }
                    }}
                />

                {/* Task Cancel Alert */}
                <TaskCompleteDialog
                    mode="cancel"
                    open={dialogStates.confirmCancelTask}
                    onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCancelTask: open }))}
                    task={selectedTaskForAction}
                    className={isDesktop ? "w-[400px]" : ""}
                    onAction={(actionValue) => {
                        if (actionValue === "yes") {
                            handleTaskAction('CANCEL');
                        } else {
                            setSelectedTaskForAction(null);
                        }
                    }}
                />
            </TooltipProvider>
        )
    }
    // Desktop version - matching the thread detail layout
    return (
        <TooltipProvider>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className={contractDetailsClasses.dialogContent}>
                    {/* Desktop Header */}
                    <DialogHeader className={contractDetailsClasses.headerDesktop}>
                        <div className="flex items-center justify-between w-full">
                            <DialogTitle className="text-xl font-medium text-white">
                                Contract - #{contract.ContractNo} - {connection?.BizName || contract.BizName || ""}
                            </DialogTitle>
                            <div className="flex items-center gap-2">
                                {canEditContracts && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-9 w-9 p-0 text-white hover:bg-slate-700"
                                        onClick={() => {
                                            setContractEntryMode('edit')
                                            setIsContractEntryOpen(true)
                                        }}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                )}
                                <DialogClose
                                    onClick={onClose}
                                    className="h-9 w-9 p-0 rounded-md hover:bg-slate-700 flex items-center justify-center text-white"
                                >
                                    <X className="h-5 w-5" />
                                    <span className="sr-only">Close</span>
                                </DialogClose>
                            </div>
                        </div>
                    </DialogHeader>
                    
                     {/* Main Content */}
                    <div className={contractDetailsClasses.contentScrollable}>
                        <div className=" gap-6 p-4">
                            <div className="space-y-3">
                               <h3 className={`text-lg font-semibold ${contractDetailsClasses.textColor}`}>
                                    Contract Details
                                </h3>
                                <Card className="border-gray-200 bg-white !pt-4">
                                    <CardContent className="pb-4 px-4">
                                        <div className="space-y-3">
                                            {/* Badge Information */}
                                            <div className="flex flex-wrap gap-2 items-start">
                                                {getBadgeData().map((badge, index) => (
                                                    <div
                                                        key={index}
                                                        className="inline-block bg-gray-100 px-3 py-2 rounded text-sm text-foreground whitespace-nowrap"
                                                    >
                                                        <span>{badge.label} </span>
                                                        <span className={`font-semibold ${badge.className}`}>{badge.value}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Notes */}
                                            {contract.Notes && (
                                                <div className="inline-block bg-gray-100 px-3 py-2 rounded text-sm text-foreground break-words whitespace-normal max-w-full">
                                                    <span>Notes: </span>
                                                    <span className="font-semibold text-gray-900">{contract.Notes}</span>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </div>
                    {TasksTable()}

                    {/* Bottom Actions Bar - Desktop */}
                    <div className="px-4 pb-4 flex justify-between gap-3">
                        <div className="flex gap-3">
                            {/* Renew Button */}
                            <Button
                                onClick={() => {
                                    setContractEntryMode('renew')
                                    setIsContractEntryOpen(true)
                                }}
                                disabled={actionLoading.renew || contract.Status === 'Cancelled'}
                                className="bg-sahi-green border hover:border-sahi-green hover:text-sahi-green "
                            >
                                {actionLoading.renew ? (
                                    <div className={contractDetailsClasses.loader} />
                                ) : (
                                    <RotateCw className="h-3 w-3" />
                                )}
                                Renew
                            </Button>

                            <Button
                                onClick={() => {
                                    setContractEntryMode('cancel')
                                    setIsContractEntryOpen(true)
                                }}
                                disabled={actionLoading.cancel}
                                className="bg-sahi-red border hover:border-sahi-red hover:text-sahi-red "
                            >
                                {actionLoading.cancel ? (
                                    <div className={contractDetailsClasses.loader} />
                                ) : (
                                    <Ban className="h-3 w-3" />
                                )}
                                Cancel
                            </Button>

                            {/* Close Button */}
                            <Button
                                variant="secondary"
                                onClick={onClose}
                            >
                                <X className="h-3 w-3" />
                                Close
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>


            <ContractEntry
                isOpen={isContractEntryOpen}
                onClose={() => setIsContractEntryOpen(false)}
                contractID={contract.ContractID || contract.ID}
                connID={connection?.ConnID || contract.ConnID}
                companyName={connection?.BizName || contract.BizName || ""}
                prefilledData={getContractEntryData()}
                isRenewalMode={contractEntryMode === 'renew'}
                isCancellationMode={contractEntryMode === 'cancel'}
                onContractSaved={(updatedContract) => {
                    setIsContractEntryOpen(false)
                    if (onContractUpdate) {
                        onContractUpdate(updatedContract)
                    }

                    onClose()
                }}
            />
            
            {/* Task Entry Dialog for Desktop */}
            <TaskEntry
                key={editingTaskID || 'new-task'}
                dialogTitle={editingTaskID ? 'Edit Task' : 'Add Task'}
                triggerButton={false}
                open={isTaskEntryOpen}
                onOpenChange={(open) => {
                    setIsTaskEntryOpen(open);
                    if (!open) {
                        setEditingTaskID(null);
                    }
                }}
                // For editing tasks
                prefilledData={editingTaskID ? (() => {
                    const task = contractTasks.find(t => t.TaskID === editingTaskID);
                    return task ? {
                        ...formatTaskData(task),
                        connID: connection?.ConnID || contract.ConnID,
                        connectionLocked: true,
                        connectionLabel: 'Contact',
                        bizName: connection?.BizName || contract.BizName || '',
                        contactName: contract.ContactName || '',
                        ctrcIDs: [String(contract.ContractID || contract.ID)], // Changed from contracts
                        contractLocked: true,
                    } : {};
                })() : {
                    // For new tasks
                    connID: connection?.ConnID || contract.ConnID,
                    connectionLocked: true,
                    connectionLabel: 'Contact',
                    bizName: connection?.BizName || contract.BizName || '',
                    contactName: contract.ContactName || '',
                    ctrcIDs: [String(contract.ContractID || contract.ID)], // Changed from contracts
                    contractLocked: true,
                }}
                onTaskAdded={() => {
                    setIsTaskEntryOpen(false);
                    setEditingTaskID(null);
                }}
            />

            {/* Task Complete Alert */}
            <AlertBox
                open={dialogStates.confirmCompleteTask}
                onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCompleteTask: open }))}
                title="Complete Task"
                description={false ? "Do you want to add a record for this completed task?" : "Are you sure you want to complete this task?"}
                actions={[
                    {
                        label: "Cancel",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto",
                        icon: <X className="h-4 w-4" />
                    },
                    {
                        // label: "No, Just Complete",
                        label: "Complete Task",
                        variant: "action",
                        value: "complete",
                        className: "w-full sm:w-auto",
                        icon: <Check className="h-4 w-4" />
                    },
                    //  ...(selectedTaskForAction?.ConnID
                    //    ? [
                    //         {
                    //             label: "Yes, Add Record",
                    //             variant: "action",
                    //             value: "record",
                    //             className: "w-full sm:w-auto",
                    //             icon: <Plus className="h-4 w-4" />,
                    //         },
                    //     ]
                    //     : []),
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "complete") {
                        handleTaskAction('COMPLETE');
                    } else if (actionValue === "record") {
                        handleItemAction('RECORD_AND_COMPLETE', selectedTaskForAction, 'task');
                    } else {
                        setSelectedTaskForAction(null);
                    }
                }}
            />

            {/* Task Cancel Alert */}
            <TaskCompleteDialog
                mode="cancel"
                open={dialogStates.confirmCancelTask}
                onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmCancelTask: open }))}
                task={selectedTaskForAction}
                className={isDesktop ? "w-[400px]" : ""}
                onAction={(actionValue) => {
                    if (actionValue === "yes") {
                        handleTaskAction('CANCEL');
                    } else {
                        setSelectedTaskForAction(null);
                    }
                }}
            />
        </TooltipProvider>
    )
}

export default ContractDetails